import React, { createContext, useContext, useState } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Get user from localStorage or set to null
  const [currentUser, setCurrentUser] = useState(() => {
    const savedUser = localStorage.getItem('currentUser');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  // Check if user can view all branches (e.g., Admin role or 'all' branches assigned)
  const canViewAllBranches = () => {
    // Admin role can view all
    if (currentUser.roleIds.includes(1)) {
      return true;
    }
    // User with 'all' branches can view all
    if (currentUser.managedBranches && currentUser.managedBranches.includes('all')) {
      return true;
    }
    // User with null/undefined managedBranches can view all
    if (!currentUser.managedBranches || currentUser.managedBranches.length === 0) {
      return false; // No branches assigned = no access
    }
    return false;
  };

  // Get branches user can see
  const getAccessibleBranches = () => {
    if (canViewAllBranches()) {
      return null; // null means all branches
    }
    return currentUser.managedBranches || [];
  };

  // Filter customers by user's accessible branches
  const filterCustomersByBranch = (customers) => {
    if (canViewAllBranches()) {
      return customers;
    }
    
    const branches = getAccessibleBranches();
    if (!branches || branches.length === 0) {
      return [];
    }
    
    return customers.filter(customer => 
      branches.includes(customer.branch)
    );
  };

  // Filter warnings by user's accessible branches (requires customers for lookup)
  const filterWarningsByBranch = (warnings, customers) => {
    if (canViewAllBranches()) {
      return warnings;
    }
    
    const branches = getAccessibleBranches();
    if (!branches || branches.length === 0) {
      return [];
    }
    
    // Create customer to branch mapping
    const customerBranchMap = {};
    customers.forEach(c => {
      customerBranchMap[c.id] = c.branch;
      customerBranchMap[c.code] = c.branch;
    });
    
    return warnings.filter(warning => {
      const customerBranch = customerBranchMap[warning.customerId] || 
                            customerBranchMap[warning.customerCode];
      return branches.includes(customerBranch);
    });
  };

  const login = (user) => {
    setCurrentUser(user);
    localStorage.setItem('currentUser', JSON.stringify(user));
  };

  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
  };

  const changePassword = (currentPassword, newPassword) => {
    if (!currentUser) return false;
    
    // Verify current password
    if (currentUser.password !== currentPassword) {
      return false;
    }

    // Update password
    const updatedUser = {
      ...currentUser,
      password: newPassword
    };
    
    setCurrentUser(updatedUser);
    localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    return true;
  };

  const updateUserProfile = (profileData) => {
    if (!currentUser) return false;

    // Update user profile
    const updatedUser = {
      ...currentUser,
      ...profileData
    };
    
    setCurrentUser(updatedUser);
    localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    return true;
  };

  const value = {
    currentUser,
    user: currentUser, // Alias for convenience
    canViewAllBranches,
    getAccessibleBranches,
    filterCustomersByBranch,
    filterWarningsByBranch,
    login,
    logout,
    changePassword,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
